import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import { getFieldErrorMessages } from 'global/helpers/form'
import { resizeImage } from 'global/helpers/imageTransformation'
import { formatBytes } from 'global/helpers/numberFormatters'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { validateImage } from 'global/helpers/validators'
import { uniqueId } from 'lodash'
import { Fragment, type ReactElement, useCallback, useEffect, useState } from 'react'
import { ErrorCode, type FileError, type FileRejection, useDropzone } from 'react-dropzone'
import { Controller, type FieldPath, type FieldPathValue, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { type UploadFileResponse } from 'screens/StatusManager/StatusManager.service'
import KNFormDropzoneItem from './KNFormDropzoneItem'
import { type KNFormDropzoneProps } from './types'

interface FileItem {
  id: string
  file: File
  pending: boolean
  errors?: string[]
  value?: string | File | Promise<string | File>
  response?: UploadFileResponse
}

const KNFormDropzone = <T extends FieldValues>({
  name,
  control,
  setError,
  clearErrors,
  setValue,
  rules,
  label,
  size,
  sx,
  disabled,
  multiple,
  accept,
  processFile,
  minFileSize,
  maxFileSize,
  validateDocuments = false,
  onPending,
}: KNFormDropzoneProps<T>): ReactElement => {
  const { t } = useTranslation()
  const [files, setFiles] = useState<FileItem[]>([])
  const [pending, setPending] = useState(false)

  const processFileErrorMessage = (error: FileError): string => {
    switch (error.code) {
      case ErrorCode.FileInvalidType:
        return t('form.validation.incorrect_file_format')
      case ErrorCode.FileTooSmall:
        return t('form.validation.min_file_size', { min: formatBytes(minFileSize ?? 0) })
      case ErrorCode.FileTooLarge:
        return t('form.validation.max_file_size', { max: formatBytes(maxFileSize ?? 0) })
      case ErrorCode.TooManyFiles:
      default:
        return error.message
    }
  }

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const newAcceptedFiles = acceptedFiles.map((file) => ({
      id: uniqueId('file-'),
      file,
      pending: true,
    }))

    const newRejectedFiles = rejectedFiles.map((rejectedFile) => ({
      id: uniqueId('file-'),
      file: rejectedFile.file,
      pending: true,
      errors: rejectedFile.errors.map((error) => processFileErrorMessage(error)),
    }))

    setFiles(multiple ? [...files, ...newAcceptedFiles, ...newRejectedFiles] : [...newAcceptedFiles, ...newRejectedFiles])
    setPending(true)
  }, [multiple, files])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: Object.fromEntries(accept?.map(key => [key, []]) ?? []),
    multiple,
    disabled,
    minSize: minFileSize,
    maxSize: maxFileSize,
    onDrop,
  })

  const processPendingFiles = async (): Promise<void> => {
    clearErrors?.(name)
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.pending) {
          if (validateImage(file.file.type)) {
            file.file = await resizeImage(file.file)
          }
          try {
            if (processFile) {
              const processedFile = await processFile(file.file)
              if (processedFile instanceof File) {
                file.value = processedFile
              } else {
                file.value = processedFile.id
                file.response = processedFile
              }
            } else {
              file.value = file.file
            }
          } catch (error) {
            file.value = undefined
            setError?.(name, { message: getRequestErrorMessage(error) })
          }
          file.pending = false
        }
        return file
      }),
    )
    const validFiles = processedFiles.filter((file) => Boolean(file.value))
    setFiles(validFiles)
    setPending(false)
    if (setValue) {
      // https://github.com/react-hook-form/react-hook-form/discussions/7246#discussioncomment-3101599
      if (validFiles.length === 0) {
        setValue(name, null as FieldPathValue<T, FieldPath<T>>)
      } else if (multiple) {
        setValue(name, validFiles.map((file) => file.value) as FieldPathValue<T, FieldPath<T>>)
      } else {
        setValue(name, validFiles[0]!.value as FieldPathValue<T, FieldPath<T>>)
      }
    }
  }

  useEffect(() => {
    onPending?.(pending)
    if (pending) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      processPendingFiles()
    }
  }, [pending])

  useEffect(() => {
    if (disabled) {
      setFiles([])
      setPending(true)
    }
  }, [disabled])

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState: { invalid, error }, formState }): ReactElement => (
        <FormControl fullWidth sx={sx} error={invalid} disabled={formState.isSubmitting}>
          {label && (
            <FormLabel sx={{ marginBottom: 0.5, color: disabled ? 'text.disabled' : 'primary.main' }}>{label}</FormLabel>
          )}
          <Box
            {...getRootProps()}
            sx={(theme) => ({
              borderWidth: 1,
              borderStyle: 'dashed',
              borderColor: theme.palette.primary.lighter,
              borderRadius: theme.borders.borderRadius['md'],
              textAlign: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              ...(isDragActive ? {
                borderStyle: 'solid',
                outline: '3px solid',
                outlineColor: theme.palette.darkblue[5],
              } : {}),
            })}
          >
            <input {...getInputProps()} />
            <Typography component="p" variant="p5" color="primary.light" m={2}>
              {t('form.dropzone')}
            </Typography>
            {files.length > 0 && (
              <List disablePadding dense sx={{ marginBottom: '-1px' }}>
                {files.map((file, index) => (
                  <Fragment key={file.id}>
                    <Divider sx={{ borderColor: 'primary.lighter', opacity: 0.33 }} />
                    <KNFormDropzoneItem
                      key={file.id}
                      file={file.file}
                      pending={file.pending}
                      disabled={formState.isSubmitting || disabled}
                      onDelete={(): void => {
                        const newFiles = [...files]
                        newFiles.splice(index, 1)
                        setFiles(newFiles)
                        setPending(true)
                      }}
                    >
                      {file.errors?.map((error, index) => (
                        <Typography key={error} component="p" variant="p5" color="error">
                          {error}
                        </Typography>
                      ))}
                      {validateDocuments && !file.pending && !file.response?.isDocument && (
                        <Typography component="p" variant="p5" color="primary.light">
                          {t('form.not_a_document')}
                        </Typography>
                      )}
                    </KNFormDropzoneItem>
                  </Fragment>
                ))}
              </List>
            )}
          </Box>
          {error?.message && <FormHelperText>{getFieldErrorMessages(error)}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

export default KNFormDropzone
